'use client';

import React, { type JSX, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Close } from '@videoblocks/react-icons';

export interface AudioFormat {
    formatName: string;
    label: string;
    fileSize?: string;
}

interface AudioDownloadPopoverProps {
    formats: AudioFormat[];
    isDownloading: boolean;
    errorMessage?: string | null;
    onSelectFormat: (formatName: string) => Promise<void>;
    onClose: () => void;
}

function stopEvent(e: React.MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
}

function FormatButton({
    format,
    isActive,
    isPending,
    isDisabled,
    onSelect,
    onHoverStart,
    onHoverEnd,
}: {
    format: AudioFormat;
    isActive: boolean;
    isPending: boolean;
    isDisabled: boolean;
    onSelect: () => void;
    onHoverStart: () => void;
    onHoverEnd: () => void;
}): JSX.Element {
    return (
        <button
            type="button"
            disabled={isDisabled}
            onClick={(e): void => {
                stopEvent(e);
                onSelect();
            }}
            onMouseEnter={onHoverStart}
            onMouseLeave={onHoverEnd}
            className={twMerge(
                'flex h-14 flex-1 cursor-pointer flex-col items-center justify-end gap-[3px] rounded-md border border-solid border-white bg-transparent px-0 py-1 transition-colors',
                'hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60',
                isActive && 'bg-white/10',
                isPending && 'bg-white/20',
            )}
        >
            <span className="text-lg font-bold leading-[25px] text-white">{format.label}</span>
            <span className="text-xs font-semibold leading-[18px] text-gray-300">
                {isPending ? 'Starting…' : format.fileSize ?? ''}
            </span>
        </button>
    );
}

export function AudioDownloadPopover({
    formats,
    isDownloading,
    errorMessage,
    onSelectFormat,
    onClose,
}: AudioDownloadPopoverProps): JSX.Element {
    const [hoveredFormatName, setHoveredFormatName] = useState<string | null>(null);
    const [pendingFormatName, setPendingFormatName] = useState<string | null>(null);

    const handleSelectFormat = useCallback(
        (formatName: string): void => {
            setPendingFormatName(formatName);
            void onSelectFormat(formatName);
        },
        [onSelectFormat],
    );

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="audio-download-popover-title"
            // rounded-[14px]= the card's rounded-lg (16px) minus its border-2 (2px); inset-0 sits inside the
            // border, so the overlay must use the card's inner radius to nest cleanly without corner gaps
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-[14px] bg-[#1F2830]/95 p-2"
            onClick={stopEvent}
        >
            <button
                type="button"
                onClick={(e): void => {
                    stopEvent(e);
                    onClose();
                }}
                className="absolute right-4 top-4 z-10 cursor-pointer border-0 bg-transparent p-0 text-white"
                aria-label="Close download popover"
            >
                <Close className="h-4 w-4" fill="white" />
            </button>

            <h2
                id="audio-download-popover-title"
                className="m-0 text-center text-base font-semibold leading-[22px] text-white"
            >
                Download
            </h2>

            {formats.length === 0 ? (
                <div className="py-2 text-center text-sm text-gray-400">No formats available</div>
            ) : (
                <div className="flex w-full items-center gap-1 px-2">
                    {formats.map((fmt) => (
                        <FormatButton
                            key={fmt.formatName}
                            format={fmt}
                            isActive={hoveredFormatName === fmt.formatName}
                            isPending={pendingFormatName === fmt.formatName && isDownloading}
                            isDisabled={isDownloading}
                            onSelect={(): void => handleSelectFormat(fmt.formatName)}
                            onHoverStart={(): void => setHoveredFormatName(fmt.formatName)}
                            onHoverEnd={(): void => setHoveredFormatName(null)}
                        />
                    ))}
                </div>
            )}

            {errorMessage && (
                <p className="m-0 px-4 text-center text-xs font-semibold leading-[16px] text-red-300">{errorMessage}</p>
            )}
        </div>
    );
}
